import { defaultMeta } from '@/constants/defaultMeta';
import { parseDateRangeSearch } from '@/lib/parseDateRange';
import { sortBy } from '@/lib/sortBy';
import { useQuery } from '@/react-query/hooks/useQuery/useQuery';
import {
  ConversionOverviewParams,
  conversionAnalyticsQueries,
} from '@/services/dashboard/conversionAnalytics/conversionAnalytics.queries';
import { TopStoreParams } from '@/services/dashboard/conversionAnalytics/conversionAnalytics.types';
import { useSearch } from '@tanstack/react-router';

export const useConversionAnalyticsOverView = (params: ConversionOverviewParams = {}) => {
  const { data, isPending } = useQuery({
    ...conversionAnalyticsQueries.conversionOverview(params),
    select: (res) => ({
      ...res,
      data: res.data,
    }),
  });
  return { conversionAnalyticsOverview: data?.data, isLoading: isPending };
};

export const useStorePerformance = () => {
  const { clubId, createdDate, page, q, sort } = useSearch({
    from: '/(app)/dashboard/conversion-analytics/',
  });
  const parsedCreatedDate = parseDateRangeSearch(createdDate);
  const { data, isLoading } = useQuery({
    ...conversionAnalyticsQueries.storePerformance({
      clubId: clubId === 'all' ? undefined : clubId,
      search: q,
      fromDate: parsedCreatedDate.start,
      toDate: parsedCreatedDate.end,
      row: page,
      fetch: 5,
    }),
    select: (response) => ({
      ...response,
      data: sortBy(response.data, sort),
    }),
  });

  return {
    storePerformance: data?.data ?? [],
    isLoading,
    pagination: data?.meta?.pagination ?? defaultMeta,
  };
};

export const useActivityLog = () => {
  const { activeTab, clubId, createdDate, fetch, page, q, actionType, sourcePageType, userStatus, sort } = useSearch({
    from: '/(app)/dashboard/conversion-analytics/',
  });
  const parsedCreatedDate = parseDateRangeSearch(createdDate);
  const { data, isLoading } = useQuery({
    ...conversionAnalyticsQueries.activityLog({
      clubId: clubId === 'all' ? undefined : clubId,
      search: q,
      actionType: actionType === 'all' ? undefined : actionType,
      sourcePageType: sourcePageType === 'all' ? undefined : sourcePageType,
      userStatus: userStatus === 'all' ? undefined : userStatus,
      fromDate: parsedCreatedDate.start,
      toDate: parsedCreatedDate.end,
      row: page,
      fetch,
    }),
    enabled: activeTab === 'activity-log',
    select: (response) => ({
      ...response,
      data: sortBy(response.data, sort, { createdDate: 'dateTime' }),
    }),
  });

  return {
    activityLogs: data?.data ?? [],
    isLoading,
    pagination: data?.meta?.pagination ?? defaultMeta,
  };
};

export const useTopStoreList = (params: TopStoreParams) => {
  const { data, isLoading } = useQuery({
    ...conversionAnalyticsQueries.topStoreList(params),
  });

  return {
    topStores: data?.data ?? [],
    isLoading,
  };
};
